// src/services/sendDoc.js — every message this browser writes goes through here.
//
// 🟥 TWO THINGS EVERY SEND HAD TO DO, AND MOST DID NEITHER.
//
//    1. SAY WHERE IT CAME FROM. The app draws a small globe on what I sent from
//       the web (services/messageOrigin), and it can only know if the message
//       says so. Sealed kinds carry `senderDeviceId` (a browser's is `web_…`);
//       plaintext kinds — a flight card — carried nothing, so the phone could not
//       even tell my web-sent flight from an echo of its own, and dropped it.
//
//    2. LEAVE MY PHONE A COPY WHILE THE MESSAGE STILL EXISTS. The recipient's
//       phone deletes a delivered message from the cloud within a second. The
//       old catch-up (ChatWindow → mirrorOut) re-READ the document from the
//       cloud after the fact and lost that race — and never ran at all for a
//       forward or a share into a chat that was not open. Here the row is copied
//       from what we are WRITING, in the same moment, with nothing to re-read.
//
// 🟢 ONE CALL SITE CHANGE PER SEND: `setDoc(ref, data)` → `setMessageDoc(ref, data)`,
//    `addDoc(col, data)` → `addMessageDoc(col, data)`. Nothing else moves.

import { doc, collection, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getIdentity } from '../crypto/identity';
import { mirrorSentRow } from './mirrorOut';

export const SENT_FROM = 'web';

/* the device id, once — a send must never wait on it twice */
let _devP = null;
async function webDeviceId() {
  if (!_devP) _devP = getIdentity().then((i) => i?.deviceId || null).catch(() => { _devP = null; return null; });
  return _devP;
}

/** Synchronous best guess for the socket relay (set by the first send). */
let _devCached = null;
export function cachedWebDeviceId() { return _devCached; }
/** The device id, resolved (and cached for the synchronous reader above). */
export async function warmWebDeviceId() { const d = await webDeviceId(); if (d) _devCached = d; return d; }

/** The fields that say "typed on the web" — added to the row, never overriding what the row says. */
export async function originFields(data) {
  const dev = await webDeviceId();
  if (dev) _devCached = dev;
  return {
    sentFrom: SENT_FROM,
    /* a sealed row already names its device (the one its keys were sealed by);
       a plaintext one gets ours, so "from my other device" is answerable */
    ...(!data?.senderDeviceId && dev ? { senderDeviceId: dev } : {}),
  };
}

/** setDoc for a message document — origin stamped, my other devices left a copy. */
export async function setMessageDoc(ref, data, opts) {
  const row = { ...data, ...(await originFields(data)) };
  await (opts ? setDoc(ref, row, opts) : setDoc(ref, row));
  try {
    const chatId = ref?.parent?.parent?.id;
    if (chatId && row.senderId) mirrorSentRow(row.senderId, chatId, ref.id, row).catch(() => {});
  } catch { /* the catch-up path in ChatWindow still runs */ }
  return ref;
}

/** addDoc for a message collection — same as setMessageDoc, with a generated id. */
export async function addMessageDoc(colRef, data) {
  const ref = doc(colRef || collection(db, 'chats', '_', 'messages'));
  await setMessageDoc(ref, data);
  return ref;
}
